import { motion } from 'framer-motion';
import { Sparkles, Eye, Shield } from 'lucide-react';
import { siteRoute } from '@/lib/domains';
import '@/styles/nymphe-v3.css';

// ============================================================================
// FADE IN COMPONENT
// ============================================================================
const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ delay, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

// ============================================================================
// PREMIUM PAGE
// ============================================================================
const PremiumPage = () => {
  const features = [
    {
      icon: Sparkles,
      title: 'Boost visibility',
      desc: 'Rise above the rest. Be seen first.'
    },
    {
      icon: Eye,
      title: 'Control presence',
      desc: 'Browse invisibly. Reveal on your terms.'
    },
    {
      icon: Shield,
      title: 'Premium signals',
      desc: 'Stand out with verified intent.'
    }
  ];

  return (
    <div className="nv3-page" data-testid="premium-page">
      <div className="noise-overlay" />

      {/* HERO */}
      <section className="nv3-premium-hero" data-testid="premium-hero">
        <div className="nv3-hero-content">
          <motion.h1
            className="nv3-hero-headline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 1 }}
            style={{ fontSize: 'clamp(2.5rem, 8vw, 4.5rem)' }}
          >
            Premium
          </motion.h1>
          <motion.p
            className="nv3-hero-sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9 }}
          >
            Elevate your presence. Control the experience.
          </motion.p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="nv3-premium-features" data-testid="premium-features">
        <div className="nv3-premium-features-inner">
          <div className="nv3-premium-features-grid">
            {features.map((feature, i) => (
              <FadeIn key={i} delay={0.1 * i}>
                <div className="nv3-premium-feature">
                  <div className="nv3-premium-feature-icon">
                    <feature.icon size={24} />
                  </div>
                  <h3 className="nv3-premium-feature-title">{feature.title}</h3>
                  <p className="nv3-premium-feature-desc">{feature.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="nv3-final-cta" data-testid="premium-cta">
        <div className="nv3-final-cta-inner">
          <FadeIn>
            <h2 className="nv3-final-cta-headline">Get access first.</h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="nv3-final-cta-sub">Premium unlocks after entry.</p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <a href={siteRoute('/access')} className="nv3-btn-primary" data-testid="premium-request-access">
              Request Access
            </a>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default PremiumPage;
